// src/store/slices/houseConnectionSlice.js

import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import api from "../../services/AxiosInstance";

// ============================================================
// CREATE REQUEST
// POST /api/house-connection/:home_id/request
// Panchayath is resolved by AxiosInstance + panMiddleware.
// ============================================================

export const createHouseConnectionRequestThunk =
  createAsyncThunk(
    "houseConnection/createRequest",
    async (
      {
        home_id,
        payload,
      },
      { rejectWithValue }
    ) => {
      try {
        const res =
          await api.post(
            `/api/house-connection/${home_id}/request`,
            payload
          );

        if (
          res.data?.success ===
          false
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed to submit connection request"
          );
        }

        return res.data;
      } catch (error) {
        return rejectWithValue(
          error?.response?.data
            ?.message ||
            error?.message ||
            "Failed to submit connection request"
        );
      }
    }
  );

// ============================================================
// GET REQUEST LIST
// GET /api/house-connection/requests
// ============================================================

export const fetchHouseConnectionRequestsThunk =
  createAsyncThunk(
    "houseConnection/fetchRequests",
    async (
      filters = {},
      { rejectWithValue }
    ) => {
      try {
        const res =
          await api.get(
            "/api/house-connection/requests",
            {
              params: {
                page:
                  filters.page ||
                  1,

                pageSize:
                  filters.pageSize ||
                  20,

                status:
                  filters.status ||
                  "",

                action_type:
                  filters.action_type ||
                  "",

                home_id:
                  filters.home_id ||
                  "",

                search:
                  filters.search ||
                  "",

                assigned_to_me:
                  filters.assigned_to_me
                    ? 1
                    : "",
              },
            }
          );

        if (
          res.data?.success ===
          false
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed to load connection requests"
          );
        }

        return {
          rows:
            res.data?.data ||
            [],

          pagination:
            res.data?.pagination ||
            {},
        };
      } catch (error) {
        return rejectWithValue(
          error?.response?.data
            ?.message ||
            error?.message ||
            "Failed to load connection requests"
        );
      }
    }
  );

// ============================================================
// GET SINGLE REQUEST
// GET /api/house-connection/requests/:request_id
// ============================================================

export const fetchHouseConnectionRequestByIdThunk =
  createAsyncThunk(
    "houseConnection/fetchRequestById",
    async (
      request_id,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await api.get(
            `/api/house-connection/requests/${request_id}`
          );

        if (
          res.data?.success ===
          false
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed to load request"
          );
        }

        return (
          res.data?.data ||
          null
        );
      } catch (error) {
        return rejectWithValue(
          error?.response?.data
            ?.message ||
            error?.message ||
            "Failed to load request"
        );
      }
    }
  );

// ============================================================
// APPROVE REQUEST
// PUT /api/house-connection/requests/:request_id/approve
// ============================================================

export const approveHouseConnectionRequestThunk =
  createAsyncThunk(
    "houseConnection/approveRequest",
    async (
      {
        request_id,
        approval_remarks = "",
      },
      { rejectWithValue }
    ) => {
      try {
        const res =
          await api.put(
            `/api/house-connection/requests/${request_id}/approve`,
            {
              approval_remarks,
            }
          );

        if (
          res.data?.success ===
          false
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed to approve request"
          );
        }

        return res.data;
      } catch (error) {
        return rejectWithValue(
          error?.response?.data
            ?.message ||
            error?.message ||
            "Failed to approve request"
        );
      }
    }
  );

// ============================================================
// REJECT REQUEST
// PUT /api/house-connection/requests/:request_id/reject
// ============================================================

export const rejectHouseConnectionRequestThunk =
  createAsyncThunk(
    "houseConnection/rejectRequest",
    async (
      {
        request_id,
        approval_remarks,
      },
      { rejectWithValue }
    ) => {
      try {
        const res =
          await api.put(
            `/api/house-connection/requests/${request_id}/reject`,
            {
              approval_remarks,
            }
          );

        if (
          res.data?.success ===
          false
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed to reject request"
          );
        }

        return res.data;
      } catch (error) {
        return rejectWithValue(
          error?.response?.data
            ?.message ||
            error?.message ||
            "Failed to reject request"
        );
      }
    }
  );

// ============================================================
// HOUSE CONNECTION HISTORY
// GET /api/house-connection/:home_id/history
// ============================================================

export const fetchHouseConnectionHistoryThunk =
  createAsyncThunk(
    "houseConnection/fetchHistory",
    async (
      home_id,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await api.get(
            `/api/house-connection/${home_id}/history`
          );

        if (
          res.data?.success ===
          false
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed to load connection history"
          );
        }

        return (
          res.data?.data ||
          []
        );
      } catch (error) {
        return rejectWithValue(
          error?.response?.data
            ?.message ||
            error?.message ||
            "Failed to load connection history"
        );
      }
    }
  );

// ============================================================
// INITIAL STATE
// ============================================================

const initialState = {
  rows: [],
  selectedRequest: null,
  history: [],

  pagination: {
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 1,
  },

  loading: false,
  detailLoading: false,
  historyLoading: false,

  submitting: false,
  approving: false,
  rejecting: false,

  error: null,

  actionSuccess: false,
  actionMessage: "",
};

// ============================================================
// SLICE
// ============================================================

const houseConnectionSlice =
  createSlice({
    name: "houseConnection",

    initialState,

    reducers: {
      clearHouseConnectionError:
        (state) => {
          state.error = null;
        },

      clearHouseConnectionAction:
        (state) => {
          state.actionSuccess = false;
          state.actionMessage = "";
        },

      clearSelectedHouseConnectionRequest:
        (state) => {
          state.selectedRequest = null;
        },

      clearHouseConnectionHistory:
        (state) => {
          state.history = [];
        },

      resetHouseConnection:
        () => ({
          ...initialState,

          pagination: {
            ...initialState.pagination,
          },
        }),
    },

    extraReducers:
      (builder) => {
        builder
          .addCase(
            createHouseConnectionRequestThunk.pending,
            (state) => {
              state.submitting = true;
              state.error = null;
              state.actionSuccess = false;
              state.actionMessage = "";
            }
          )

          .addCase(
            createHouseConnectionRequestThunk.fulfilled,
            (state, action) => {
              state.submitting = false;
              state.actionSuccess = true;

              state.actionMessage =
                action.payload?.message ||
                "Connection request submitted successfully";
            }
          )

          .addCase(
            createHouseConnectionRequestThunk.rejected,
            (state, action) => {
              state.submitting = false;
              state.actionSuccess = false;

              state.error =
                action.payload ||
                "Failed to submit connection request";
            }
          );

        builder
          .addCase(
            fetchHouseConnectionRequestsThunk.pending,
            (state) => {
              state.loading = true;
              state.error = null;
            }
          )

          .addCase(
            fetchHouseConnectionRequestsThunk.fulfilled,
            (state, action) => {
              state.loading = false;

              state.rows =
                action.payload?.rows ||
                [];

              state.pagination = {
                ...state.pagination,
                ...(action.payload?.pagination || {}),
              };
            }
          )

          .addCase(
            fetchHouseConnectionRequestsThunk.rejected,
            (state, action) => {
              state.loading = false;
              state.rows = [];

              state.error =
                action.payload ||
                "Failed to load connection requests";
            }
          );

        builder
          .addCase(
            fetchHouseConnectionRequestByIdThunk.pending,
            (state) => {
              state.detailLoading = true;
              state.error = null;
            }
          )

          .addCase(
            fetchHouseConnectionRequestByIdThunk.fulfilled,
            (state, action) => {
              state.detailLoading = false;

              state.selectedRequest =
                action.payload ||
                null;
            }
          )

          .addCase(
            fetchHouseConnectionRequestByIdThunk.rejected,
            (state, action) => {
              state.detailLoading = false;
              state.selectedRequest = null;

              state.error =
                action.payload ||
                "Failed to load request";
            }
          );

        builder
          .addCase(
            approveHouseConnectionRequestThunk.pending,
            (state) => {
              state.approving = true;
              state.error = null;
              state.actionSuccess = false;
            }
          )

          .addCase(
            approveHouseConnectionRequestThunk.fulfilled,
            (state, action) => {
              state.approving = false;
              state.actionSuccess = true;

              state.actionMessage =
                action.payload?.message ||
                "Request approved successfully";
            }
          )

          .addCase(
            approveHouseConnectionRequestThunk.rejected,
            (state, action) => {
              state.approving = false;
              state.actionSuccess = false;

              state.error =
                action.payload ||
                "Failed to approve request";
            }
          );

        builder
          .addCase(
            rejectHouseConnectionRequestThunk.pending,
            (state) => {
              state.rejecting = true;
              state.error = null;
              state.actionSuccess = false;
            }
          )

          .addCase(
            rejectHouseConnectionRequestThunk.fulfilled,
            (state, action) => {
              state.rejecting = false;
              state.actionSuccess = true;

              state.actionMessage =
                action.payload?.message ||
                "Request rejected successfully";
            }
          )

          .addCase(
            rejectHouseConnectionRequestThunk.rejected,
            (state, action) => {
              state.rejecting = false;
              state.actionSuccess = false;

              state.error =
                action.payload ||
                "Failed to reject request";
            }
          );

        builder
          .addCase(
            fetchHouseConnectionHistoryThunk.pending,
            (state) => {
              state.historyLoading = true;
              state.error = null;
            }
          )

          .addCase(
            fetchHouseConnectionHistoryThunk.fulfilled,
            (state, action) => {
              state.historyLoading = false;

              state.history =
                Array.isArray(action.payload)
                  ? action.payload
                  : [];
            }
          )

          .addCase(
            fetchHouseConnectionHistoryThunk.rejected,
            (state, action) => {
              state.historyLoading = false;
              state.history = [];

              state.error =
                action.payload ||
                "Failed to load connection history";
            }
          );
      },
  });

// ============================================================
// EXPORT
// ============================================================

export const {
  clearHouseConnectionError,
  clearHouseConnectionAction,
  clearSelectedHouseConnectionRequest,
  clearHouseConnectionHistory,
  resetHouseConnection,
} =
  houseConnectionSlice.actions;

export default
  houseConnectionSlice.reducer;
